import { useState } from 'react';
import { CHORD_CATEGORIES, CHORD_TYPES, type ChordTypeId } from '@/music/chords';
import { pitchClassOf } from '@/music/notes';
import { analyzeMelodicLine } from '@/music/melody';
import {
  frettedMidi,
  guideStringLabel,
  topStringForFingering,
  type StringSet,
} from '@/music/tuning';
import {
  primaryVoicingFamily,
  voicingFamilyLabel,
  type VoicingFamily,
} from '@/music/voicingFamilies';
import { enumerateChordVoicings, topNoteOf, type ChordVoicing } from '@/music/voicing';
import { useStore } from '@/state/store';
import { ChordTypeSelect, NoteSelect } from '@/components/pickers';
import { PlayableDiagram } from '@/components/PlayableDiagram';

interface MelodyMatch {
  voicing: ChordVoicing;
  root: string;
  chordType: ChordTypeId;
  substituted: boolean;
}

/** Find an inversion whose top voice equals the target note. */
function findVoicingWithTopNote(
  root: string,
  chordType: ChordTypeId,
  note: string,
  stringSet: StringSet,
  voicingFamily: VoicingFamily,
): ChordVoicing | null {
  const targetPc = pitchClassOf(note);
  for (const voicing of enumerateChordVoicings(root, chordType, stringSet, voicingFamily)) {
    const top = voicing ? topNoteOf(voicing, stringSet) : null;
    if (voicing && top && pitchClassOf(top) === targetPc) return voicing;
  }
  return null;
}

/** Try the requested chord, then qualities in its category, to put `note` on top. */
function findMelodyChord(
  root: string,
  chordType: ChordTypeId,
  note: string,
  stringSet: StringSet,
  voicingFamily: VoicingFamily,
): MelodyMatch | null {
  const exact = findVoicingWithTopNote(root, chordType, note, stringSet, voicingFamily);
  if (exact) return { voicing: exact, root, chordType, substituted: false };

  const category = CHORD_TYPES[chordType].category;
  const siblings = CHORD_CATEGORIES.find((c) => c.category === category)?.types ?? [];
  for (const sibling of siblings) {
    if (sibling === chordType) continue;
    const voicing = findVoicingWithTopNote(root, sibling, note, stringSet, voicingFamily);
    if (voicing) return { voicing, root, chordType: sibling, substituted: true };
  }
  return null;
}

export function MelodyPage() {
  const stringSet = useStore((s) => s.stringSet);
  const voicingFamily = primaryVoicingFamily(useStore((s) => s.voicingFamily));
  const [note, setNote] = useState('E');
  const [root, setRoot] = useState('C');
  const [chordType, setChordType] = useState<ChordTypeId>('maj7');
  const [match, setMatch] = useState<MelodyMatch | null>(null);
  const [searched, setSearched] = useState(false);
  const [line, setLine] = useState<MelodyMatch[]>([]);

  const find = () => {
    setMatch(findMelodyChord(root, chordType, note, stringSet, voicingFamily));
    setSearched(true);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Top-note finder</h1>
        <p>
          Finds a {voicingFamilyLabel(voicingFamily)} voicing with the melody note on top
          {voicingFamily === 'joe-pass'
            ? ''
            : ` (the ${guideStringLabel(stringSet, voicingFamily)})`}
          .
        </p>
      </div>

      <div className="control-bar">
        <div className="control-group">
          <span className="label">Melody note</span>
          <NoteSelect value={note} onChange={setNote} aria-label="Melody note" />
        </div>
        <div className="control-group">
          <span className="label">Chord root</span>
          <NoteSelect value={root} onChange={setRoot} aria-label="Chord root" />
        </div>
        <div className="control-group">
          <span className="label">Chord type</span>
          <ChordTypeSelect value={chordType} onChange={setChordType} aria-label="Chord type" />
        </div>
        <button className="btn btn-primary btn-sm" onClick={find} style={{ alignSelf: 'flex-end' }}>
          Find voicing
        </button>
      </div>

      {searched && !match && (
        <p className="empty-hint">
          No voicing or substitute puts {note} on top of {root}
          {CHORD_TYPES[chordType].symbol} on this string layout.
        </p>
      )}

      {match && (
        <section style={{ marginBottom: 24 }}>
          {match.substituted && (
            <p className="muted">
              {root}
              {CHORD_TYPES[chordType].symbol} cannot have {note} on top here. Using{' '}
              <strong>
                {match.root}
                {CHORD_TYPES[match.chordType].symbol}
              </strong>{' '}
              instead (same chord category).
            </p>
          )}
          <div style={{ maxWidth: 200 }}>
            <PlayableDiagram
              chord={{
                fingering: match.voicing,
                rootDisplay: match.root,
                chordType: match.chordType,
                symbol: match.voicing.symbol,
                inversion: match.voicing.inversion,
                // The voicing remembers the set it was found on — the global
                // set may have changed since, which would corrupt the diagram.
                stringSet: match.voicing.stringSet,
                leadNote: note,
                targetTopNote: note,
              }}
              caption={`melody ${note}`}
              highlightAvoid
            />
          </div>
          <button
            className="btn btn-sm"
            style={{ marginTop: 10 }}
            onClick={() => setLine((l) => [...l, match])}
          >
            Add to melody line
          </button>
        </section>
      )}

      {line.length > 0 && (
        <section>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2 className="section-title" style={{ border: 0 }}>
              Melody line
            </h2>
            <button className="btn btn-sm btn-danger" onClick={() => setLine([])}>
              Clear line
            </button>
          </div>
          <div className="melody-line-chords">
            {line.map((m, i) => (
              <PlayableDiagram
                key={i}
                variant="bare"
                chord={{
                  fingering: m.voicing,
                  rootDisplay: m.root,
                  chordType: m.chordType,
                  symbol: m.voicing.symbol,
                  inversion: m.voicing.inversion,
                  stringSet: m.voicing.stringSet,
                  leadNote: topNoteOf(m.voicing),
                }}
              />
            ))}
          </div>
          {line.length >= 2 && <MelodyLineAnalysis line={line} />}
        </section>
      )}
    </div>
  );
}

function MelodyLineAnalysis({ line }: { line: MelodyMatch[] }) {
  // Each voicing carries the string set it was found on (the global set may
  // have changed since), so the top voice is read per match.
  const notes = line.map((m) => topNoteOf(m.voicing) ?? '?');
  const pitches = line
    .map((m) => {
      const topString = topStringForFingering(m.voicing);
      const fret = m.voicing.frets[topString];
      return fret === null || fret === undefined ? null : frettedMidi(topString, fret);
    })
    .filter((p): p is number => p !== null);
  const analysis = analyzeMelodicLine(pitches);

  return (
    <div className="analysis-panel" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Melody line analysis</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        {notes.join(' → ')}
      </p>
      <div>
        <strong>{analysis.smoothnessPct}%</strong> of moves are steps or common tones ·{' '}
        {analysis.stepwise} step{analysis.stepwise === 1 ? '' : 's'}, {analysis.leaps} leap
        {analysis.leaps === 1 ? '' : 's'}, {analysis.commonTones} common tone
        {analysis.commonTones === 1 ? '' : 's'}
      </div>
      <div className="guide-line-sequence">
        {analysis.steps.map((s, i) => (
          <div className="guide-line-note" key={i}>
            <strong>
              {notes[i]}→{notes[i + 1]}
            </strong>
            <span>{s.kind === 'common-tone' ? 'common' : s.kind}</span>
            <span>
              {s.direction === 'same' ? '·' : s.direction === 'up' ? '▲' : '▼'}{' '}
              {Math.abs(s.semitones)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
